import type { PublicUser } from '@shared/schemas.js';

/**
 * What the application shell should do, given everything currently known about
 * the session and the connection.
 *
 * This is pure and dependency-free on purpose. The behaviour it encodes is the
 * part of Phase 4 that is easy to get wrong and expensive when it is wrong — a
 * VA typing call notes for four minutes and losing them to a redirect — so it
 * is expressed as a function that can be tested exhaustively rather than as
 * conditions scattered through a component tree.
 *
 * The four situations that must NOT be conflated:
 *
 *   1. Initial anonymous visit — enforcement is on and nobody has signed in.
 *      Send them to the login page. There is nothing on screen to protect.
 *   2. Explicit logout — the person chose to leave. Same treatment; preserving
 *      a screen they deliberately walked away from would be surprising.
 *   3. Mid-session expiry — they WERE signed in and a request came back 401.
 *      Keep the screen exactly as it is and ask for the password over the top
 *      of it.
 *   4. Offline — no response at all. Keep the screen, say so, change nothing
 *      about the session.
 */

export interface SessionGateInput {
  /** Server-reported enforcement. While false, everything renders. */
  authEnabled: boolean;
  /** The signed-in user, as last known. */
  user: PublicUser | null;
  /** Set when a 401 arrived AFTER we had a user. See `unauthorizedOutcome`. */
  sessionExpired: boolean;
  /** True when the last request never reached a server. */
  offline: boolean;
  /** The initial /auth/me probe has not answered yet. */
  isLoading: boolean;
}

export type SessionGateDecision =
  /** Waiting on the first /auth/me. */
  | 'loading'
  /** Render the application normally. */
  | 'render'
  /** Send to /login. Only for cases 1 and 2 above. */
  | 'redirect'
  /**
   * Render the application AND a blocking reauthentication dialog over it.
   * The route stays mounted; component state survives.
   */
  | 'reauthenticate'
  /** A temporary password must be replaced before anything else. */
  | 'change-password';

export function sessionGate(input: SessionGateInput): SessionGateDecision {
  if (input.isLoading) return 'loading';

  // Enforcement off: LeadForge behaves exactly as it did before Phase 1.
  if (!input.authEnabled) return 'render';

  // Case 3 wins over case 1 even though `user` may still be populated: the
  // whole point is that the screen stays up while the password is re-entered.
  if (input.sessionExpired) return 'reauthenticate';

  if (!input.user) {
    // Case 4 must not be mistaken for case 1. A dropped link cannot tell us
    // anything about the session, and redirecting on it throws away work for a
    // problem that fixes itself.
    if (input.offline) return 'render';
    // Cases 1 and 2.
    return 'redirect';
  }

  if (input.user.mustChangePassword) return 'change-password';

  return 'render';
}

/**
 * What a 401 means, which depends entirely on whether we had a session.
 *
 * Called from the fetch layer's unauthorized listener. Kept beside the gate so
 * the two halves of the rule cannot drift.
 */
export function unauthorizedOutcome(state: {
  authEnabled: boolean;
  hasUser: boolean;
}): 'expire' | 'ignore' {
  // With enforcement off the only endpoint that can 401 is the enforcement
  // switch itself. That is a permission error on one button, not a dead
  // session, and must not put a password prompt in front of an anonymous user.
  if (!state.authEnabled) return 'ignore';
  // No user means the anonymous probe already told us to redirect; there is no
  // session to have expired.
  if (!state.hasUser) return 'ignore';
  return 'expire';
}

/**
 * User-facing copy for the two whole-application failures.
 *
 * These strings live here, next to the logic and reachable from a test, for
 * one specific reason: an earlier draft of the offline banner promised the
 * work "will save once the connection returns". Nothing in LeadForge queues a
 * write or replays a failed mutation, so that was a promise the application
 * could not keep — and the person believing it would close the tab.
 *
 * Anything written here must be literally true of the code as it stands.
 */
export const CONNECTION_COPY = {
  offline:
    'Cannot reach LeadForge. Your unsaved work is still on screen. Reconnect before submitting it — nothing is saved until you do.',
  sessionExpiredTitle: 'Your session expired',
  sessionExpiredBody:
    'Sign in again to continue. This page and anything you have typed are still here — nothing was submitted, so retry the action after signing in.',
} as const;

/**
 * Phrases that would make the offline copy a lie.
 *
 * A regression list rather than a clever check: these are the specific
 * reassurances a future edit is tempted to add, and each one describes a
 * feature this phase deliberately does not have (no queue, no replay, no
 * background sync).
 */
export const FORBIDDEN_OFFLINE_PROMISES = [
  'will save',
  'will be saved',
  'saved automatically',
  'save automatically',
  'automatically save',
  'queued',
  'queue your',
  'retried automatically',
  'retry automatically',
  'sync when',
  'synced when',
  'resend',
  'once the connection returns',
  'when you reconnect it will',
] as const;
